/**
 * SYNCED FROM SERVER — do not edit by hand.
 *
 * Source: `server/src/api/<domain>/<domain>.contract.ts` (or the
 * shared bases under `server/src/api/common/contracts/`).
 *
 * Regenerate: `cd api-client && bun run sync:contracts`.
 *
 * The server is the source of truth for contract definitions. This
 * file exists so the web SPA can import zod schemas + oRPC contract
 * types from `@event-manager/api-client/contracts/<domain>.contract`
 * without reaching into the server workspace.
 */

/**
 * Dynamic field definitions (Phase 4 §4.4)
 *
 * Shape + runtime helper for building a strict Zod object from a list of
 * `EventTypeFieldDefinition` rows. Consumed by both the server (validating
 * Booking.customFieldValues on create/modify) and the frontend (react-hook-form
 * resolver on the booking drawer). Single source of truth.
 *
 * See `docs/plans/design-21-eventtype-dynamic-fields.md` §4.2–§4.4.
 */

import * as z from "zod";

// =============================================================================
// FieldType enum — mirrors the Prisma enum `FieldType` exactly.
// =============================================================================

export const FieldTypeSchema = z.enum([
	"TEXT",
	"NUMBER",
	"DATE",
	"BOOL",
	"SELECT",
	"MULTI_SELECT",
]);

export type FieldType = z.infer<typeof FieldTypeSchema>;

// =============================================================================
// Option + validation sub-schemas
// =============================================================================

/**
 * Option shape for SELECT / MULTI_SELECT field types. `i18nKey` is optional
 * and lets the frontend resolve the label through translations without
 * storing multiple localized strings on the field definition itself.
 */
export const FieldOptionSchema = z.object({
	value: z.string().min(1).max(100),
	label: z.string().min(1).max(200),
	i18nKey: z.string().max(200).optional(),
});

export type FieldOption = z.infer<typeof FieldOptionSchema>;

/**
 * Validation config (min/max/pattern/step) applied to the generated Zod node.
 */
export const FieldValidationSchema = z
	.object({
		min: z.number().optional(),
		max: z.number().optional(),
		pattern: z.string().max(500).optional(),
		step: z.number().optional(),
	})
	.strict();

export type FieldValidation = z.infer<typeof FieldValidationSchema>;

// =============================================================================
// EventTypeFieldDefinition — the metadata row itself.
// =============================================================================

/**
 * Input schema (used by `eventTypes.fields.add` and `fields.update`).
 */
export const EventTypeFieldDefinitionInputSchema = z
	.object({
		name: z
			.string()
			.min(1)
			.max(60)
			.regex(
				/^[a-z][a-z0-9_]*$/,
				"Field name must be lower_snake_case and start with a letter"
			),
		label: z.string().min(1).max(200),
		type: FieldTypeSchema,
		required: z.boolean().default(false),
		position: z.number().int().min(0).default(0),
		options: z.array(FieldOptionSchema).optional(),
		validation: FieldValidationSchema.optional(),
		gridVisible: z.boolean().default(true),
		gridWidth: z.number().int().min(40).max(1000).optional(),
	})
	.strict();

export type EventTypeFieldDefinitionInput = z.infer<
	typeof EventTypeFieldDefinitionInputSchema
>;

/**
 * Output shape — what the server returns for a single field definition.
 */
export const EventTypeFieldDefinitionSchema = z.object({
	id: z.string(),
	eventTypeId: z.string(),
	orgId: z.string(),
	name: z.string(),
	label: z.string(),
	type: FieldTypeSchema,
	required: z.boolean(),
	position: z.number().int(),
	options: z.array(FieldOptionSchema).nullable(),
	validation: FieldValidationSchema.nullable(),
	gridVisible: z.boolean(),
	gridWidth: z.number().int().nullable(),
	createdAt: z.string().datetime(),
	updatedAt: z.string().datetime(),
	deletedAt: z.string().datetime().nullable(),
});

export type EventTypeFieldDefinition = z.infer<
	typeof EventTypeFieldDefinitionSchema
>;

// =============================================================================
// zodSchemaFromDefinitions — runtime builder
// =============================================================================

/**
 * Minimum shape a definition needs to participate in schema generation.
 * We accept a subset (not the full `EventTypeFieldDefinition`) so callers can
 * hand in trimmed rows (e.g. a cache projection without timestamps).
 */
interface MinimalDefinition {
	readonly name: string;
	readonly options?: readonly FieldOption[] | null;
	readonly required: boolean;
	readonly type: FieldType;
	readonly validation?: FieldValidation | null;
}

const applyValidation = (
	node: z.ZodType,
	validation: FieldValidation | null | undefined,
	type: FieldType
): z.ZodType => {
	if (!validation) {
		return node;
	}
	// Zod's typed method overloads diverge per node kind; we cast through the
	// runtime surface after guarding on the field type.
	let out: z.ZodType = node;
	if (type === "TEXT") {
		const asStr = out as unknown as z.ZodString;
		if (typeof validation.min === "number") {
			out = asStr.min(validation.min);
		}
		if (typeof validation.max === "number") {
			out = (out as z.ZodString).max(validation.max);
		}
		if (validation.pattern) {
			out = (out as z.ZodString).regex(new RegExp(validation.pattern));
		}
	} else if (type === "NUMBER") {
		const asNum = out as unknown as z.ZodNumber;
		if (typeof validation.min === "number") {
			out = asNum.min(validation.min);
		}
		if (typeof validation.max === "number") {
			out = (out as z.ZodNumber).max(validation.max);
		}
		if (typeof validation.step === "number") {
			out = (out as z.ZodNumber).multipleOf(validation.step);
		}
	} else if (type === "MULTI_SELECT") {
		const asArr = out as unknown as z.ZodArray<z.ZodTypeAny>;
		if (typeof validation.min === "number") {
			out = asArr.min(validation.min);
		}
		if (typeof validation.max === "number") {
			out = (out as z.ZodArray<z.ZodTypeAny>).max(validation.max);
		}
	}
	return out;
};

const nodeForField = (def: MinimalDefinition): z.ZodType => {
	switch (def.type) {
		case "TEXT":
			return z.string();
		case "NUMBER":
			return z.number().finite();
		case "DATE":
			return z.coerce.date();
		case "BOOL":
			return z.boolean();
		case "SELECT": {
			const values = (def.options ?? []).map((o) => o.value);
			if (values.length === 0) {
				// Degenerate case — no options means no valid value.
				// Keep the object strict: reject any value.
				return z.never();
			}
			return z.enum(values as [string, ...string[]]);
		}
		case "MULTI_SELECT": {
			const values = (def.options ?? []).map((o) => o.value);
			if (values.length === 0) {
				return z.array(z.never());
			}
			return z.array(z.enum(values as [string, ...string[]]));
		}
		default:
			throw new Error(`Unhandled field type: ${String(def.type)}`);
	}
};

/**
 * Build a strict Zod object from a list of field definitions.
 *
 * - Required fields are included as-is.
 * - Optional fields are `.optional().nullable()` (absent or null both parse).
 * - The resulting object is `.strict()` — unknown keys fail. The caller is
 *   expected to strip deleted-field keys via the tolerant-read pattern in
 *   §4.2 (read: parse loosely, write: parse strictly).
 */
export function zodSchemaFromDefinitions(
	defs: readonly MinimalDefinition[]
): z.ZodObject<Record<string, z.ZodTypeAny>> {
	const shape: Record<string, z.ZodTypeAny> = {};
	for (const def of defs) {
		let node = nodeForField(def);
		node = applyValidation(node, def.validation ?? null, def.type);
		shape[def.name] = def.required ? node : node.optional().nullable();
	}
	return z.object(shape).strict();
}

/**
 * Tolerant variant used on the read path (§4.2 step 3): unknown keys are
 * stripped rather than erroring, so the response survives deleted fields
 * without losing the rest of the payload.
 */
export function zodSchemaFromDefinitionsTolerant(
	defs: readonly MinimalDefinition[]
): z.ZodObject<Record<string, z.ZodTypeAny>> {
	const shape: Record<string, z.ZodTypeAny> = {};
	for (const def of defs) {
		let node = nodeForField(def);
		node = applyValidation(node, def.validation ?? null, def.type);
		// Always optional on read — deletions shouldn't break parse.
		shape[def.name] = node.optional().nullable();
	}
	return z.object(shape);
}
